import { useMemo } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/typography'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import { cn } from '@/lib/utils'
import type { Staff } from '@/lib/api/directory.client'
import { formatPhone } from '../formatters'
import { memberColor } from './rotation/member-color'

export function RoleMembersSection(props: {
  allStaff: Staff[]
  orderedIds: string[]
  onChange: (ids: string[]) => void
  rotationMode: boolean
}) {
  const { allStaff, orderedIds, onChange, rotationMode } = props

  const staffById = useMemo(
    () => new Map(allStaff.map(staff => [staff.id, staff])),
    [allStaff],
  )
  const selected = useMemo(() => new Set(orderedIds), [orderedIds])
  const pickerItems = useMemo<SearchDropdownItem<Staff>[]>(
    () =>
      allStaff
        .filter(staff => !selected.has(staff.id))
        .map(staff => ({
          id: staff.id,
          label: staff.name,
          sublabel: [formatPhone(staff.phone), staff.email]
            .filter(Boolean)
            .join(' · '),
          data: staff,
        })),
    [allStaff, selected],
  )

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const from = orderedIds.indexOf(active.id as string)
    const to = orderedIds.indexOf(over.id as string)
    if (from === -1 || to === -1) return
    onChange(arrayMove(orderedIds, from, to))
  }

  return (
    <section className="space-y-3">
      <Heading variant="subsection-title">Members</Heading>

      <SearchDropdown<Staff>
        items={pickerItems}
        multiSelect={false}
        placeholder="Add a person…"
        searchPlaceholder="Search by name, phone, or email"
        emptyMessage={
          allStaff.length === 0
            ? 'No staff yet — add some on the Staff tab.'
            : 'No matching staff'
        }
        buttonClassName="w-full max-w-md"
        onSelect={item => {
          if (item && !Array.isArray(item) && !orderedIds.includes(item.id)) {
            onChange([...orderedIds, item.id])
          }
        }}
      />

      {orderedIds.length === 0 ? (
        <div className="rounded-md border px-3 py-6 text-center text-sm text-muted-foreground">
          {allStaff.length === 0
            ? 'No staff yet — add some on the Staff tab.'
            : 'No one in this role yet. Search above to add people.'}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={orderedIds}
              strategy={verticalListSortingStrategy}
            >
              {orderedIds.map((id, index) => {
                const staff = staffById.get(id)
                if (!staff) return null
                return (
                  <MemberRow
                    key={id}
                    id={id}
                    index={index}
                    colorIndex={index}
                    staff={staff}
                    showOrder={!rotationMode}
                    showColor={rotationMode}
                    onRemove={() =>
                      onChange(orderedIds.filter(existing => existing !== id))
                    }
                  />
                )
              })}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </section>
  )
}

function MemberRow(props: {
  id: string
  index: number
  colorIndex: number
  staff: Staff
  showOrder: boolean
  showColor: boolean
  onRemove: () => void
}) {
  const { id, index, colorIndex, staff, showOrder, showColor, onRemove } = props
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 border-b bg-background px-2 py-1.5 last:border-b-0',
        isDragging && 'opacity-60',
      )}
    >
      <button
        type="button"
        className="cursor-grab touch-none text-muted-foreground hover:text-foreground active:cursor-grabbing"
        aria-label={`Reorder ${staff.name}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      {showOrder && (
        <span className="w-5 text-xs tabular-nums text-muted-foreground">
          {index + 1}
        </span>
      )}
      {showColor && (
        <span
          className={cn(
            'h-2.5 w-2.5 shrink-0 rounded-full',
            memberColor(colorIndex).swatch,
          )}
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{staff.name}</div>
        {staff.phone && (
          <div className="truncate text-xs text-muted-foreground">
            {formatPhone(staff.phone)}
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={onRemove}
        aria-label={`Remove ${staff.name}`}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  )
}
